import { useState, useEffect } from "react";
import api from "../../Services/Api"; 
import StatCard from "../StatCard/StatCard";
import QuickActions from "../QuickActions/QuickActions";
import DailyQuoteWidget from "../MotivationalQuote/DailyQuoteWidget";
import styles from './StatsDashboard.module.css';

function StatsDashboard({ userData }) {

    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isLogging, setIsLogging] = useState(false);
    const [message, setMessage] = useState(null);
    const [motivation, setMotivation] = useState(null);
    const [newBadges, setNewBadges] = useState([]);

    const userLocalDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const response = await api.get('stats/overview/');
            setStats(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to load stats. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleLogClean = async () => {
        setIsLogging(true);
        setMessage(null);
        setMotivation(null);
        setNewBadges([]);

        try {
            const response = await api.post('log_clean/', { 
                type: 'clean',
                date: userLocalDate 
            });

            const { created, motivation: motivationData, new_badges } = response.data;

            if (created) {
                // First time logging today - show motivation and badges
                if (motivationData) {
                    setMotivation(motivationData);
                }

                if (new_badges && new_badges.length > 0) {
                    setNewBadges(new_badges);
                }

                setMessage({
                    type: 'success',
                    text: '✅ Clean day logged successfully!'
                });

                // Refresh stats only if it's a new log
                fetchStats();
            } else {
                // Already logged today - show different message
                setMessage({
                    type: 'info',
                    text: '📅 Already logged clean for today!'
                });
            }

        } catch (err) {
            // Handle error response
            if (err.response && err.response.data && err.response.data.error) {
                setMessage({
                    type: 'error',
                    text: `❌ ${err.response.data.error}`
                });
            } else {
                setMessage({
                    type: 'error',
                    text: '❌ Failed to log clean day. Please try again.'
                });
            }
        } finally {
            setIsLogging(false);
        }
    };

    const handleLogRelapse = async () => {
        setIsLogging(true);
        setMessage(null);
        setMotivation(null);
        setNewBadges([]);

        try {
            const response = await api.post('log_fap/', { 
                type: 'relapse',
                date: userLocalDate 
            });

            const { created, motivation: motivationData, fap_count, message: responseMessage } = response.data;

            if (created) {
                // First relapse of the day - show motivation
                if (motivationData) {
                    setMotivation(motivationData);
                }

                setMessage({
                    type: 'warning',
                    text: '📝 Relapse logged. Tomorrow is a fresh start!'
                });
            } else {
                // Additional relapse - show count
                setMessage({
                    type: 'info',
                    text: `📊 Additional relapse logged (${fap_count} today)`
                });
            }

            fetchStats();
        } catch (err) {
            // Handle error response
            if (err.response && err.response.data && err.response.data.error) {
                setMessage({
                    type: 'error',
                    text: `❌ ${err.response.data.error}`
                });
            } else {
                setMessage({
                    type: 'error',
                    text: '❌ Failed to log. Please try again.'
                });
            }
        } finally {
            setIsLogging(false);
        }
    };

    const dismissMotivation = () => {
        setMotivation(null);
        setNewBadges([]);
    };

    const handleRefresh = () => {
        fetchStats();
    };

    if (loading) {
        return (
            <div className={styles.dashboard}>
                <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <p>Loading your recovery statistics...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return <div className={styles.error}>{error}</div>;
    }

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().toLocaleString('default', { month: 'long' });
    const currentDate = new Date().getDate();

    return (
        <div className={styles.dashboard}>

            {/* MOTIVATION MODAL */}
            {motivation && (
                <div className={styles.motivationOverlay} onClick={dismissMotivation}>
                    <div className={styles.motivationModal} onClick={(e) => e.stopPropagation()}>
                        {/* New Badges */}
                        {newBadges.length > 0 && (
                            <div className={styles.newBadgesSection}>
                                <h3 className={styles.badgesTitle}>🎉 New Badge{newBadges.length > 1 ? 's' : ''} Earned!</h3>
                                <div className={styles.badgesList}>
                                    {newBadges.map((badge, idx) => (
                                        <div key={idx} className={styles.newBadgeItem}>
                                            <span className={styles.badgeIcon}>{badge.icon}</span>
                                            <div className={styles.badgeDetails}>
                                                <span className={styles.badgeName}>{badge.name}</span>
                                                <span className={styles.badgePoints}>+{badge.points} pts</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Main Message */}
                        <div className={styles.motivationMessage}>
                            {motivation.message}
                        </div>

                        {/* Comparison Stats */}
                        {motivation.comparison && Object.keys(motivation.comparison).length > 0 && (
                            <div className={styles.comparisonSection}>
                                {motivation.comparison.friends && (
                                    <p className={styles.comparisonItem}>{motivation.comparison.friends}</p>
                                )}
                                {motivation.comparison.community && (
                                    <p className={styles.comparisonItem}>{motivation.comparison.community}</p>
                                )}
                                {motivation.comparison.encouragement && (
                                    <p className={styles.comparisonItem}>{motivation.comparison.encouragement}</p>
                                )}
                            </div>
                        )}

                        {/* Current Streak Display */}
                        {motivation.is_clean && (
                            <div className={styles.streakDisplay}>
                                <span className={styles.streakNumber}>{motivation.current_streak}</span>
                                <span className={styles.streakLabel}>Day Streak 🔥</span>
                            </div>
                        )}

                        <button className={styles.dismissBtn} onClick={dismissMotivation}>
                            Continue
                        </button>
                    </div>
                </div>
            )}

            {/* HEADER */}
            <div className={styles.header}>
                <h2>Dashboard</h2>
                <p className={styles.subtitle}>
                    Track your progress, celebrate your victories
                </p>

               
            </div>

            {/* MESSAGE */}
            {message && (
                <div className={`${styles.message} ${styles[message.type]}`}>
                    {message.text}
                </div>
            )}

            {/* QUICK ACTIONS */}
            <QuickActions
                onLogClean={handleLogClean}
                onLogRelapse={handleLogRelapse}
                onRefresh={handleRefresh}
                isLoading={isLogging}
            />
{/* FOOTER TITLE */}
            <div className={styles.StatsType}>
                <h2>
                    Overall Statistics Till {currentMonth} {currentDate}, {currentYear}
                </h2>
                 
            </div>
            {/* STATS GRID */}
            <div className={styles.grid}>
                <StatCard
                    title="Fap Days"
                    value={stats?.basic_stats?.total_faps ?? 0}
                    icon="📉"
                    color="#e74c3c"
                    type="warning"
                    progress={stats?.basic_stats?.total_faps ?? 0}
                    maxProgress={(stats?.basic_stats?.total_faps ?? 0) + (stats?.basic_stats?.clean_days ?? 0) || 1}
                />
                <StatCard
                    title="Clean Days"
                    value={stats?.basic_stats?.clean_days ?? 0}
                    icon="✅"
                    color="#2ecc71"
                    type="positive"
                    progress={stats?.basic_stats?.clean_days ?? 0}
                    maxProgress={(stats?.basic_stats?.total_faps ?? 0) + (stats?.basic_stats?.clean_days ?? 0) || 1}
                />
                <StatCard
                    title="Current Streak"
                    value={stats?.basic_stats?.current_streak ?? 0}
                    icon="🔥"
                    color="#3498db"
                    type="neutral"
                    subtitle="days"
                    progress={stats?.basic_stats?.current_streak ?? 0}
                    maxProgress={stats?.personal_records?.longest_streak || 30}
                />
                <StatCard
                    title="Best Streak"
                    value={stats?.personal_records?.longest_streak ?? 0}
                    icon="🏆"
                    color="#9b59b6"
                    type="special"
                    subtitle="personal record"
                    progress={100}
                    maxProgress={100}
                />
            </div>

            <div className={styles.Quote}>
                    <DailyQuoteWidget showRefresh={true} compact={false} />
                </div>

        </div>
    );
}

export default StatsDashboard;
